import { Building2, Gem } from "lucide-react";
import { useMemo, useState } from "react";
import { Link } from "@tanstack/react-router";
import { Layout } from "@/components/Layout";
import { getCities, type Country } from "@/lib/gold-data";

type Metal = "gold" | "silver";

type Props = {
  country: Country;
  metal: Metal;
};

/** Silver alias slugs that actually have a dedicated route file. */
const SILVER_SLUG_MAP: Record<string, string> = {
  "hyderabad-pk": "hyderabad-pakistan",
  "hyderabad-in": "hyderabad-india",
};

const SILVER_ROUTE_SLUGS = new Set([
  "ahmedabad", "bahawalpur", "bangalore", "chennai", "faisalabad",
  "gujranwala", "hyderabad-india", "hyderabad-pakistan", "islamabad",
  "kanpur", "karachi", "kolkata", "lahore", "multan", "mumbai", "nagpur",
  "peshawar", "pune", "quetta", "rawalpindi", "sargodha", "sialkot", "surat",
]);

const COPY = {
  pakistan: {
    lang: "ur",
    dir: "rtl" as const,
    title: { gold: "اپنے شہر میں سونے کا آج کا ریٹ", silver: "اپنے شہر میں چاندی کا آج کا ریٹ" },
    intro: {
      gold: "نیچے سے اپنا شہر منتخب کریں — ہر شہر کا 24K، 22K اور 21K لائیو ریٹ، چارٹ، اور مکمل تفصیل۔",
      silver: "نیچے سے اپنا شہر منتخب کریں — ہر شہر کا لائیو چاندی ریٹ، چارٹ، اور تفصیلی معلومات۔",
    },
    search: "شہر تلاش کریں…",
    select: "شہر منتخب کریں",
    noResults: "کوئی شہر نہیں ملا",
    go: "دیکھیں",
  },
  india: {
    lang: "hi",
    dir: "ltr" as const,
    title: { gold: "अपने शहर में आज का सोने का भाव", silver: "अपने शहर में आज का चांदी का भाव" },
    intro: {
      gold: "नीचे से अपना शहर चुनें — हर शहर का 24K, 22K और 21K लाइव रेट, चार्ट और पूरी जानकारी।",
      silver: "नीचे से अपना शहर चुनें — हर शहर का लाइव चांदी रेट, चार्ट और पूरी जानकारी।",
    },
    search: "शहर खोजें…",
    select: "शहर चुनें",
    noResults: "कोई शहर नहीं मिला",
    go: "देखें",
  },
};

function cityHref(country: Country, metal: Metal, slug: string): string | null {
  if (metal === "gold") return `/${country}/${slug}`;
  const silverSlug = SILVER_SLUG_MAP[slug] ?? slug;
  if (!SILVER_ROUTE_SLUGS.has(silverSlug)) return null;
  return `/silver-rate-${silverSlug}`;
}

function cityRoute(country: Country, metal: Metal, slug: string) {
  const href = cityHref(country, metal, slug);
  if (!href) return null;
  if (metal === "gold") return { to: "/$country/$city" as const, params: { country, city: slug } };
  return { to: href as never };
}

export function CityPicker({ country, metal }: Props) {
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState<string>("");
  const copy = COPY[country];

  const cities = useMemo(() => {
    const all = getCities(country)
      .map((c) => ({ ...c, href: cityHref(country, metal, c.slug) }))
      .filter((c): c is typeof c & { href: string } => c.href !== null);
    const needle = q.trim().toLowerCase();
    if (!needle) return all;
    return all.filter((c) => c.name.toLowerCase().includes(needle) || c.slug.includes(needle));
  }, [country, metal, q]);

  const selectedHref = selected ? cityHref(country, metal, selected) : null;

  const metalLabel = metal === "gold"
    ? country === "pakistan" ? "Gold" : "Gold"
    : "Silver";

  return (
    <Layout>
      <section className="mx-auto max-w-4xl px-4 py-8">
        <nav className="text-xs text-muted-foreground mb-3">
          <Link to="/" className="hover:underline">Home</Link> ›{" "}
          <Link to={country === "pakistan" ? "/pakistan" : "/india"} className="hover:underline">
            {country === "pakistan" ? "Pakistan" : "India"}
          </Link>{" "}
          › {metalLabel} · Choose city
        </nav>

        <h1
          lang={copy.lang}
          dir={copy.dir}
          className="text-3xl md:text-4xl font-bold leading-snug"
          style={copy.lang === "ur" ? { fontFamily: "'Noto Nastaliq Urdu', system-ui, serif" } : undefined}
        >
          {copy.title[metal]}
        </h1>
        <p
          lang={copy.lang}
          dir={copy.dir}
          className="mt-2 text-base text-muted-foreground leading-relaxed"
        >
          {copy.intro[metal]}
        </p>

        {/* Search + dropdown row */}
        <div className="mt-6 grid gap-3 sm:grid-cols-[1fr_auto]">
          <input
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={copy.search}
            aria-label={copy.search}
            className="w-full rounded-xl border border-border bg-card px-4 py-3 text-base outline-none focus:border-primary"
          />
          <div className="flex gap-2">
            <select
              value={selected}
              onChange={(e) => setSelected(e.target.value)}
              aria-label={copy.select}
              className="rounded-xl border border-border bg-card px-3 py-3 text-base outline-none focus:border-primary min-w-[140px]"
            >
              <option value="">{copy.select}</option>
              {cities.map((c) => (
                <option key={c.slug} value={c.slug}>{c.name}</option>
              ))}
            </select>
            <button
              type="button"
              disabled={!selectedHref}
              onClick={() => { if (selectedHref) window.location.assign(selectedHref); }}
              className={
                selectedHref
                  ? "rounded-xl bg-primary px-4 py-3 text-sm font-bold text-primary-foreground hover:bg-primary/90 whitespace-nowrap"
                  : "rounded-xl bg-muted px-4 py-3 text-sm font-bold text-muted-foreground cursor-not-allowed whitespace-nowrap"
              }
            >
              {copy.go} →
            </button>
          </div>
        </div>

        {/* City grid */}
        <div className="mt-6">
          {cities.length === 0 ? (
            <p className="text-center py-10 text-muted-foreground" lang={copy.lang} dir={copy.dir}>
              {copy.noResults}
            </p>
          ) : (
            <ul className="grid gap-2 grid-cols-2 sm:grid-cols-3 md:grid-cols-4">
              {cities.map((c) => {
                const route = cityRoute(country, metal, c.slug);
                if (!route) return null;
                const content = (
                  <>
                    {metal === "gold" ? <Building2 className="h-4 w-4 text-gold" aria-hidden /> : <Gem className="h-4 w-4 text-muted-foreground" aria-hidden />}
                    <span>{c.name}</span>
                  </>
                );
                const className = "flex min-h-[48px] items-center justify-center gap-2 rounded-lg border border-border bg-card px-3 py-3 text-center text-sm font-medium hover:border-primary hover:bg-primary/5 transition-colors";
                return (
                  <li key={c.slug}>
                    {"params" in route && route.params ? (
                      <Link to="/$country/$city" params={route.params} className={className}>{content}</Link>
                    ) : (
                      <Link to={route.to} className={className}>{content}</Link>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </section>
    </Layout>
  );
}